"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight, CalendarDays, MapPin, Clock } from "lucide-react";

type CalEvent = {
  id: string;
  slug: string;
  title: string;
  startDate: string; // ISO string
  endDate: string | null;
  location: string | null;
  coverImageUrl: string | null;
  category: { name: string } | null;
};

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

function startOfMonth(year: number, month: number) {
  return new Date(year, month, 1);
}
function daysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
// ISO weekday: Mon=0..Sun=6
function isoWeekday(date: Date) {
  return (date.getDay() + 6) % 7;
}
function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function AgendaCalendar({ events }: { events: CalEvent[] }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selected, setSelected] = useState<Date | null>(null);

  const firstDay = startOfMonth(year, month);
  const totalDays = daysInMonth(year, month);
  const offset = isoWeekday(firstDay); // blanks before day 1

  // Events of this month, keyed by day-of-month
  const eventsByDay = new Map<number, CalEvent[]>();
  for (const ev of events) {
    const d = new Date(ev.startDate);
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate();
      if (!eventsByDay.has(day)) eventsByDay.set(day, []);
      eventsByDay.get(day)!.push(ev);
    }
  }

  function prevMonth() {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
    setSelected(null);
  }
  function nextMonth() {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
    setSelected(null);
  }

  const selectedEvents = selected
    ? events.filter(ev => sameDay(new Date(ev.startDate), selected))
    : [];

  return (
    <div className="space-y-4">
      {/* Header navigation */}
      <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
        <button onClick={prevMonth} className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50">
          <ChevronLeft size={16} />
        </button>
        <h3 className="text-base font-semibold text-slate-800">
          {MONTHS[month]} {year}
        </h3>
        <button onClick={nextMonth} className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-50">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Grid */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-slate-100">
          {DAYS.map((d) => (
            <div key={d} className="py-2.5 text-center text-xs font-bold uppercase tracking-wider text-slate-400">
              {d}
            </div>
          ))}
        </div>

        {/* Cells */}
        <div className="grid grid-cols-7">
          {/* Blank offset cells */}
          {Array.from({ length: offset }).map((_, i) => (
            <div key={`blank-${i}`} className="border-b border-r border-slate-50 p-2 min-h-[60px] bg-slate-50/40" />
          ))}

          {/* Day cells */}
          {Array.from({ length: totalDays }).map((_, i) => {
            const day = i + 1;
            const date = new Date(year, month, day);
            const isToday = sameDay(date, today);
            const isSelected = selected ? sameDay(date, selected) : false;
            const dayEvents = eventsByDay.get(day) ?? [];
            const hasEvents = dayEvents.length > 0;
            const col = (offset + i) % 7;
            const isWeekend = col === 5 || col === 6;

            return (
              <button
                key={day}
                onClick={() => setSelected(isSelected ? null : date)}
                className={[
                  "relative flex min-h-[60px] flex-col items-start p-2 text-left transition border-b border-r border-slate-100",
                  isSelected ? "bg-[#1a2744]" : isToday ? "bg-blue-50" : isWeekend ? "bg-slate-50/60" : "hover:bg-slate-50",
                ].join(" ")}
              >
                <span className={[
                  "mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold",
                  isSelected ? "bg-white text-[#1a2744]" : isToday ? "bg-blue-500 text-white" : "text-slate-700",
                ].join(" ")}>
                  {day}
                </span>
                {hasEvents && (
                  <div className="flex flex-wrap gap-0.5">
                    {dayEvents.slice(0, 3).map((ev) => (
                      <span
                        key={ev.id}
                        className={[
                          "block h-1.5 w-1.5 rounded-full",
                          isSelected ? "bg-white/70" : "bg-[#1a2744]",
                        ].join(" ")}
                      />
                    ))}
                    {dayEvents.length > 3 && (
                      <span className={["text-[9px] font-bold leading-none", isSelected ? "text-white/70" : "text-slate-400"].join(" ")}>
                        +{dayEvents.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day events */}
      {selected && (
        <div className="space-y-3">
          <p className="text-sm font-semibold text-slate-700">
            {selected.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
            {" — "}
            {selectedEvents.length === 0 ? "Aucun événement" : `${selectedEvents.length} événement${selectedEvents.length > 1 ? "s" : ""}`}
          </p>
          {selectedEvents.map((ev) => (
            <Link
              key={ev.id}
              href={`/agenda/${ev.slug}`}
              className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md"
            >
              {ev.coverImageUrl && (
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={ev.coverImageUrl} alt="" className="h-full w-full object-cover" />
                </div>
              )}
              {!ev.coverImageUrl && (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-slate-100">
                  <CalendarDays size={20} className="text-slate-400" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-800">{ev.title}</p>
                <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Clock size={11} />
                    {new Date(ev.startDate).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  {ev.location && (
                    <span className="flex items-center gap-1">
                      <MapPin size={11} />
                      {ev.location}
                    </span>
                  )}
                  {ev.category && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                      {ev.category.name}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Legend */}
      <p className="text-center text-xs text-slate-400">
        Cliquez sur un jour pour voir ses événements · Les points indiquent la présence d'événements
      </p>
    </div>
  );
}
